import { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { FaExternalLinkAlt, FaDatabase } from 'react-icons/fa'
import { useSocketState } from '../../helpers/useSocket'
import BracketSet from '../../components/Bracket/BracketSet'
import groupBy from 'lodash/groupBy'
import { getBackendUrl } from '../../helpers/url'

// Base URL for backend requests from environment variable
const BACKEND_BASE_URL = getBackendUrl()

function buildProgressionMap(sets) {
	const map = new Map()
	for (const set of sets) {
		for (const slot of set.slots ?? []) {
			if (String(slot.prereqType).toLowerCase() !== 'set') continue
			if (slot.prereqId == null) continue
			const key = String(slot.prereqId)
			const entry = map.get(key) ?? {}
			if (Number(slot.prereqPlacement) === 1) entry.winner = set
			else if (Number(slot.prereqPlacement) === 2) entry.loser = set
			map.set(key, entry)
		}
	}
	return map
}

const BOX_HEIGHT = 48
const MIN_GAP = 24
const TOP_PADDING = 8

const PITCH = BOX_HEIGHT + MIN_GAP

// double elim has negative round numbers
function bracketSide(set) {
	return Number(set.round) < 0 ? -1 : 1
}

function compareIdentifier(a, b) {
	const x = String(a.identifier ?? '')
	const y = String(b.identifier ?? '')
	return x.length - y.length || x.localeCompare(y)
}

function layoutPhaseGroup(sets) {
	const byRound = groupBy(sets, 'round')
	const rounds = Object.keys(byRound).sort((a, b) => Number(a) - Number(b))
	const byId = new Map(sets.map((set) => [String(set.id), set]))

	const childrenById = new Map()
	const childIds = new Set()
	for (const set of sets) {
		const children = []
		for (const slot of set.slots ?? []) {
			if (String(slot.prereqType).toLowerCase() !== 'set') continue
			if (Number(slot.prereqPlacement) !== 1) continue
			const feeder = byId.get(String(slot.prereqId))
			if (!feeder || feeder === set || children.includes(feeder)) continue
			if (bracketSide(feeder) !== bracketSide(set)) continue
			children.push(feeder)
			childIds.add(String(feeder.id))
		}
		childrenById.set(String(set.id), children)
	}

	const centerById = new Map()
	if (childIds.size === 0) {
		for (const round of rounds) {
			byRound[round].forEach((set, i) => centerById.set(String(set.id), TOP_PADDING + BOX_HEIGHT / 2 + i * PITCH))
		}
	} else {
		let nextLeaf = 0
		const place = (set) => {
			const id = String(set.id)
			if (centerById.has(id)) return centerById.get(id)
			centerById.set(id, 0)
			const childCenters = childrenById.get(id).map(place)
			const center = childCenters.length ? (Math.min(...childCenters) + Math.max(...childCenters)) / 2 : TOP_PADDING + BOX_HEIGHT / 2 + nextLeaf++ * PITCH
			centerById.set(id, center)
			return center
		}

		const roots = sets.filter((set) => !childIds.has(String(set.id))).sort((a, b) => bracketSide(b) - bracketSide(a) || compareIdentifier(a, b))
		let side = null
		for (const root of roots) {
			// Losers-bracket columns are separate from winners-bracket ones, so start them back at the top.
			if (side !== null && bracketSide(root) !== side) nextLeaf = 0
			side = bracketSide(root)
			place(root)
		}
	}

	const setsByRound = new Map()
	const marginTopById = new Map()
	for (const round of rounds) {
		const ordered = [...byRound[round]].sort((a, b) => centerById.get(String(a.id)) - centerById.get(String(b.id)))
		let prevBottom = null
		ordered.forEach((set, idx) => {
			let top = centerById.get(String(set.id)) - BOX_HEIGHT / 2
			const minTop = idx === 0 ? TOP_PADDING : prevBottom + MIN_GAP
			if (top < minTop) top = minTop
			marginTopById.set(String(set.id), idx === 0 ? top : top - prevBottom)
			prevBottom = top + BOX_HEIGHT
		})
		setsByRound.set(round, ordered)
	}

	return { rounds, setsByRound, marginTopById }
}

const WINNER_COLOR = '#4ade80'
const LOSER_COLOR = '#ef4444'
const SECTION_GAP = 32
const STUB = 8

function connectorPath(c) {
	if (c.channelY == null) {
		const midX = (c.x1 + c.x2) / 2
		return `M ${c.x1} ${c.y1} H ${midX} V ${c.y2} H ${c.x2}`
	}
	return `M ${c.x1} ${c.y1} H ${c.x1 + STUB} V ${c.channelY} H ${c.x2 - STUB} V ${c.y2} H ${c.x2}`
}

function PhaseBracket({ phase }) {
	const containerRef = useRef(null)
	const boxRefs = useRef(new Map())
	const slotRefs = useRef(new Map())
	const lowerSectionRefs = useRef(new Map())
	const [connectors, setConnectors] = useState([])

	const sets = useMemo(() => (phase.phaseGroups?.nodes ?? []).flatMap((pg) => pg.sets?.nodes ?? []), [phase])
	const progressionMap = useMemo(() => buildProgressionMap(sets), [sets])
	const setInfoById = useMemo(() => {
		const info = new Map()
		for (const pg of phase.phaseGroups?.nodes ?? []) {
			for (const set of pg.sets?.nodes ?? []) {
				info.set(String(set.id), { set, phaseGroupId: String(pg.id) })
			}
		}
		return info
	}, [phase])

	useLayoutEffect(() => {
		function recompute() {
			const containerEl = containerRef.current
			if (!containerEl) return
			const containerRect = containerEl.getBoundingClientRect()

			const kinds = phase.bracketType === 'DOUBLE_ELIMINATION' ? ['winner'] : ['winner', 'loser']
			const next = []
			for (const [sourceId, entry] of progressionMap.entries()) {
				for (const kind of kinds) {
					const dest = entry[kind]
					if (!dest) continue

					const slotIndex = (dest.slots ?? []).findIndex(
						(slot) => String(slot.prereqId) === String(sourceId) && Number(slot.prereqPlacement) === (kind === 'winner' ? 1 : 2),
					)
					if (slotIndex < 0) continue

					const boxEl = boxRefs.current.get(String(sourceId))
					const slotEl = slotRefs.current.get(`${dest.id}:${slotIndex}`)
					if (!boxEl || !slotEl) continue

					const boxRect = boxEl.getBoundingClientRect()
					const slotRect = slotEl.getBoundingClientRect()

					let channelY = null
					const source = setInfoById.get(String(sourceId))
					if (source && bracketSide(source.set) !== bracketSide(dest)) {
						const lowerEl = lowerSectionRefs.current.get(source.phaseGroupId)
						if (lowerEl) {
							channelY = lowerEl.getBoundingClientRect().top - containerRect.top - SECTION_GAP / 2
						}
					}

					next.push({
						key: `${sourceId}-${kind}`,
						x1: boxRect.right - containerRect.left,
						y1: boxRect.top + boxRect.height / 2 - containerRect.top,
						x2: slotRect.left - containerRect.left,
						y2: slotRect.top + slotRect.height / 2 - containerRect.top,
						channelY,
						kind,
					})
				}
			}
			setConnectors(next)
		}

		recompute()
		// Collapsed phases mount under display:none, where everything measures
		// 0x0; re-measure once the container gets a real size (and on reflow).
		const observer = new ResizeObserver(recompute)
		if (containerRef.current) observer.observe(containerRef.current)
		return () => observer.disconnect()
	}, [progressionMap, setInfoById, phase.bracketType])

	function registerBoxRef(setId, el) {
		if (el) boxRefs.current.set(String(setId), el)
		else boxRefs.current.delete(String(setId))
	}

	function registerSlotRef(setId, slotIndex, el) {
		const key = `${setId}:${slotIndex}`
		if (el) slotRefs.current.set(key, el)
		else slotRefs.current.delete(key)
	}

	function renderRounds(rounds, layout) {
		return rounds.map((round) => {
			const roundSets = layout.setsByRound.get(round)
			const title = roundSets[0]?.fullRoundText || `Round ${round}`
			return (
				<div key={round} className={`round-group round_${round} w-[188px]`}>
					<h3 className="h-6 px-3 truncate" title={title}>
						{title}
					</h3>
					{roundSets.map((set) => (
						<div
							key={set.id}
							style={{
								marginTop: layout.marginTopById.get(String(set.id)),
							}}>
							<BracketSet
								id={set.id}
								identifier={set.identifier}
								round={set.round}
								totalGames={set.totalGames}
								slots={set.slots}
								registerBoxRef={registerBoxRef}
								registerSlotRef={registerSlotRef}
							/>
						</div>
					))}
				</div>
			)
		})
	}

	return (
		<div className="overflow-x-auto">
			<div ref={containerRef} className="relative flex flex-row w-max">
				<svg className="absolute inset-0 pointer-events-none" width="100%" height="100%" style={{ overflow: 'visible' }}>
					{connectors.map((c) => (
						<path key={c.key} d={connectorPath(c)} fill="none" stroke={c.kind === 'winner' ? WINNER_COLOR : LOSER_COLOR} strokeWidth="1.5" />
					))}
				</svg>
				{phase.phaseGroups?.nodes.map((phaseGroup, phaseIndex) => {
					const layout = layoutPhaseGroup(phaseGroup.sets.nodes)
					const upperRounds = layout.rounds.filter((round) => Number(round) >= 0)
					const lowerRounds = layout.rounds.filter((round) => Number(round) < 0).sort((a, b) => Number(b) - Number(a))
					return (
						<div key={phaseGroup.id} className="flex flex-col">
							<a className="" href={phaseGroup.bracketUrl} target="_blank" rel="noreferrer">
								Pool #{phaseIndex + 1} - {phaseGroup.id}
							</a>
							{lowerRounds.length === 0 ? (
								<div className={`ml-8 ${phase.bracketType} ${phase.bracketType}_${phase.numSeeds} flex`}>{renderRounds(layout.rounds, layout)}</div>
							) : (
								<div className="ml-8 flex flex-col">
									<div className="flex">{renderRounds(upperRounds, layout)}</div>
									<div
										ref={(el) => {
											const key = String(phaseGroup.id)
											if (el) lowerSectionRefs.current.set(key, el)
											else lowerSectionRefs.current.delete(key)
										}}
										className="flex"
										style={{ marginTop: SECTION_GAP }}>
										{renderRounds(lowerRounds, layout)}
									</div>
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

function BracketDashboard() {
	const { socket } = useOutletContext()

	const [tournament] = useSocketState('Tournament')

	function handleTournamentSlugChange(el) {
		setTournamentSlug(el.target.value)
	}
	const [tournamentSlug, setTournamentSlug] = useState(null)

	useEffect(() => {
		if (tournament?.bracket?.slug && !tournamentSlug) {
			const cleaned = String(tournament.bracket.slug).replace('tournament/', '')
			setTournamentSlug(cleaned)
		}
	}, [tournament, tournamentSlug])

	// track expanded/collapsed state for each phase by id
	const [expandedPhases, setExpandedPhases] = useState({})

	// track seeding panel visibility per event id
	const [showSeeding, setShowSeeding] = useState({})

	function toggleSeeding(eventId) {
		setShowSeeding((prev) => ({ ...prev, [eventId]: !prev[eventId] }))
	}

	function getEventSeedGroups(event) {
		// Returns array of { phase, phaseGroup, seeds[] } — one entry per phaseGroup that has seeds
		const groups = []
		for (const phase of event.phases ?? []) {
			for (const pg of phase.phaseGroups?.nodes ?? []) {
				const seeds = (pg.seeds?.nodes ?? []).filter((s) => s.entrant).sort((a, b) => a.groupSeedNum - b.groupSeedNum)
				if (seeds.length > 0) groups.push({ phase, phaseGroup: pg, seeds })
			}
		}
		return groups
	}

	function togglePhase(id, defaultExpanded) {
		setExpandedPhases((prev) => ({
			...prev,
			[id]: typeof defaultExpanded === 'undefined' ? !prev[id] : defaultExpanded,
		}))
	}

	// track which phase is currently being refreshed (null = full bracket loading)
	const [loadingPhaseId, setLoadingPhaseId] = useState(null)

	useEffect(() => {
		if (!tournament?.bracketLoading) {
			setLoadingPhaseId(null)
		}
	}, [tournament?.bracketLoading])

	//Ideally this should be able to handle multiple different format
	//And be able to return the same slug
	//https://www.start.gg/tournament/egc-masters-finals-2025/event/main-event/brackets/2124223/3097036
	//https://www.start.gg/tournament/egc-masters-finals-2025/
	//tournament/egc-masters-finals-2025/
	//egc-masters-finals-2025
	function handleGetTournament() {
		const slug = tournamentSlug //TODO insert regex here
		socket.emit('setTournamentSlug', slug)
	}

	function handleRefreshPhase(phaseId) {
		setLoadingPhaseId(phaseId)
		socket.emit('updateBracketPhase', phaseId)
	}

	return (
		<div>
			<div className="flex flex-col mb-4">
				<label>Tournament Slug/ID</label>
				<div className="flex flex-col">
					<div className="flex flex-row items-center">
						{/*TODO Should possibly use the tournament.tournamentKey instead */}
						<input type="text" id="mapDraftUrl" name="mapDraftUrl" className="w-56 mr-8" value={tournamentSlug ?? ''} onChange={handleTournamentSlugChange} />
						<button className="h-8 my-auto mr-2" onClick={handleGetTournament} disabled={tournament?.bracketLoading}>
							Get Tournament
						</button>
						{tournament?.bracketLoading && loadingPhaseId === null && <span className="ml-2 text-sm text-gray-400">Loading...</span>}
					</div>
					<h1>Using {tournament?.bracketProvider}</h1>
				</div>
			</div>

			{tournament?.bracket &&
				tournament?.bracket?.events?.map((event) => (
					<div>
						<div className="flex items-center gap-2 mb-1">
							<h1 className="font-bold text-xl">{event.name}</h1>
							<button className="px-2 py-0.5 text-sm rounded border border-gray-300 hover:bg-gray-100" onClick={() => toggleSeeding(event.id)}>
								{showSeeding[event.id] ? 'Hide Seeding' : 'Show Seeding'}
							</button>
						</div>
						{showSeeding[event.id] &&
							(() => {
								const groups = getEventSeedGroups(event)
								return groups.length > 0 ? (
									<div className="mb-3 ml-2 flex flex-row gap-2">
										{groups.map(({ phase, phaseGroup, seeds }, i) => (
											<div key={phaseGroup.id} className="border border-gray-200 rounded p-2">
												{groups.length > 1 && (
													<div className="text-xs font-semibold text-gray-500 mb-1">
														{phase.name} — Pool #{i + 1}
													</div>
												)}
												<div className="overflow-y-auto min-w-64 max-h-64">
													<table className="text-sm w-full">
														<thead>
															<tr className="text-left text-gray-500 border-b border-gray-200">
																<th className="pr-4 pb-1">Seed</th>
																<th className="pb-1">Entrant</th>
															</tr>
														</thead>
														<tbody>
															{seeds.map((seed) => (
																<tr key={seed.entrant.id} className="border-b border-gray-100 last:border-0">
																	<td className="pr-4 py-0.5 text-gray-500 whitespace-nowrap">
																		{seed.groupSeedNum} <span className="text-gray-400">({seed.seedNum})</span>
																	</td>
																	<td className="py-0.5">{seed.entrant.name}</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</div>
										))}
									</div>
								) : (
									<p className="text-sm text-gray-400 mb-3 ml-2">No seeding data — refresh a phase first.</p>
								)
							})()}
						{event.phases.map((phase) => (
							<div className="ml-4 mb-1" key={phase.id}>
								<div className="flex items-center">
									<button
										type="button"
										onClick={() => togglePhase(phase.id)}
										className="mr-2 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200"
										aria-expanded={!!(expandedPhases[phase.id] ?? phase.state === 'ACTIVE')}
										aria-controls={`phase-${phase.id}`}>
										{(expandedPhases[phase.id] ?? phase.state === 'ACTIVE') ? '▾' : '▸'}
									</button>

									<h1 className="text-lg">{phase.name}</h1>
									<span className="mx-2 text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-gray-200 text-gray-600">{phase.bracketType}</span>
									<span className="mr-2 text-xs text-gray-400">
										{phase.state} · {phase.id}
									</span>
									<div className="flex items-center gap-1">
										<Link to={phase.bracketType === 'ROUND_ROBIN' ? `/RoundRobinDisplay?id=${phase.id}` : `/BracketDisplay?id=${phase.id}`} target="_blank">
											<button className="bg-teal-700 hover:bg-teal-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm">
												Bracket View
												<FaExternalLinkAlt className="text-xs" />
											</button>
										</Link>
										<Link to={`${BACKEND_BASE_URL}/api/vmix/startggBracket/${phase.id}`} target="_blank">
											<button className="bg-indigo-700 hover:bg-indigo-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm" title="startggBracket">
												<FaDatabase className="text-xs" />
												Bracket
											</button>
										</Link>
										{phase.bracketType === 'ROUND_ROBIN' && (
											<>
												<Link to={`${BACKEND_BASE_URL}/api/vmix/startggRoundRobinStandings/${phase.id}`} target="_blank">
													<button
														className="bg-indigo-700 hover:bg-indigo-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm"
														title="startggRoundRobinStandings">
														<FaDatabase className="text-xs" />
														Standings
													</button>
												</Link>
												<Link to={`${BACKEND_BASE_URL}/api/vmix/startggRoundRobinGrid/${phase.id}`} target="_blank">
													<button
														className="bg-indigo-700 hover:bg-indigo-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm"
														title="startggRoundRobinGrid">
														<FaDatabase className="text-xs" />
														Grid
													</button>
												</Link>
											</>
										)}
									</div>
									<button
										className="ml-2 px-2 py-0.5 text-sm rounded border border-gray-300 hover:bg-gray-100"
										onClick={() => handleRefreshPhase(phase.id)}
										disabled={tournament?.bracketLoading}>
										Update
									</button>
									{loadingPhaseId === phase.id && <span className="ml-1 text-sm text-gray-400">Loading...</span>}
									{phase.bracketType === 'SWISS' && (
										<>
											<Link to={`${BACKEND_BASE_URL}/api/vmix/onimaruStandings`} target="_blank">
												<button className="bg-indigo-700 hover:bg-indigo-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm" title="onimaruStandings">
													<FaDatabase className="text-xs" />
													Standings
												</button>
											</Link>
											<Link to={`${BACKEND_BASE_URL}/api/vmix/onimaruPlayoffs`} target="_blank">
												<button className="bg-indigo-700 hover:bg-indigo-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm" title="onimaruPlayoffs">
													<FaDatabase className="text-xs" />
													Playoffs
												</button>
											</Link>
										</>
									)}
								</div>

								<div id={`phase-${phase.id}`} className={(expandedPhases[phase.id] ?? phase.state === 'ACTIVE') ? 'mt-2' : 'mt-2 hidden'}>
									<PhaseBracket phase={phase} />
								</div>
							</div>
						))}
						<hr></hr>
					</div>
				))}
		</div>
	)
}

export default BracketDashboard
